import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  EmployerReputation,
  FreelancerReputationWithRehire,
} from '@freelancer/datastore/collections';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType, TextSize } from '@freelancer/ui/text';
import { RoleApi } from 'api-typings/common/common';
import * as Rx from 'rxjs';

export interface UserEmployerReputation {
  type: RoleApi.EMPLOYER;
  reputation: EmployerReputation;
}

export interface UserFreelancerReputation {
  type: RoleApi.FREELANCER;
  reputation: FreelancerReputationWithRehire;
}

export type UserReputation = UserEmployerReputation | UserFreelancerReputation;

@Component({
  selector: 'app-user-profile-summary-reputation',
  template: `
    <fl-bit *ngIf="userReputation$ | flAsync as userReputation; else loading">
      <ng-container *ngIf="!(isDeloitteUserProfile$ | flAsync)">
        <fl-bit
          *ngIf="userReputation.type === RoleApi.FREELANCER"
          class="ReputationColumn"
        >
          <fl-grid>
            <fl-col [col]="6">
              <app-user-profile-summary-reputation-item
                i18n-label="Jobs completed freelancer reputation label"
                label="Jobs Completed"
                [value]="
                  userReputation.reputation.entireHistory.completionRate / 100
                "
              ></app-user-profile-summary-reputation-item>
            </fl-col>
            <fl-col [col]="6">
              <app-user-profile-summary-reputation-item
                i18n-label="On budget freelancer reputation label"
                label="On Budget"
                [value]="userReputation.reputation.entireHistory.onBudget"
              ></app-user-profile-summary-reputation-item>
            </fl-col>
            <fl-col [col]="6">
              <app-user-profile-summary-reputation-item
                i18n-label="On time freelancer reputation label"
                label="On Time"
                [value]="userReputation.reputation.entireHistory.onTimeInMs"
              ></app-user-profile-summary-reputation-item>
            </fl-col>
            <fl-col [col]="6">
              <app-user-profile-summary-reputation-item
                i18n-label="Repeat hire rate freelancer reputation label"
                label="Repeat Hire Rate"
                [value]="userReputation.reputation.entireHistory.rehireRate"
              ></app-user-profile-summary-reputation-item>
            </fl-col>
          </fl-grid>
        </fl-bit>
      </ng-container>
      <fl-bit *ngIf="userReputation.type === RoleApi.EMPLOYER">
        <fl-grid *ngIf="userReputation.reputation.projectStats; else loading">
          <fl-col [col]="6">
            <app-user-profile-summary-reputation-item
              i18n-label="Open projects employer reputation label"
              label="Open projects"
              [percent]="false"
              [value]="userReputation.reputation.projectStats.open"
            ></app-user-profile-summary-reputation-item>
          </fl-col>
          <fl-col [col]="6">
            <app-user-profile-summary-reputation-item
              i18n-label="Active projects employer reputation label"
              label="Active projects"
              [percent]="false"
              [value]="userReputation.reputation.projectStats.work_in_progress"
            ></app-user-profile-summary-reputation-item>
          </fl-col>
          <fl-col [col]="6">
            <app-user-profile-summary-reputation-item
              i18n-label="Past projects employer reputation label"
              label="Past projects"
              [percent]="false"
              [value]="userReputation.reputation.entireHistory.complete"
            ></app-user-profile-summary-reputation-item>
          </fl-col>
          <fl-col [col]="6">
            <app-user-profile-summary-reputation-item
              i18n-label="Total projects employer reputation label"
              label=" Total projects"
              [percent]="false"
              [value]="
                (userReputation.reputation.projectStats.open || 0) +
                (userReputation.reputation.projectStats.work_in_progress || 0) +
                (userReputation.reputation.projectStats.complete || 0)
              "
            >
            </app-user-profile-summary-reputation-item>
          </fl-col>
        </fl-grid>
      </fl-bit>
    </fl-bit>
    <ng-template #loading>
      <fl-bit>
        <fl-grid>
          <fl-col *flRepeat="4" [col]="6">
            <app-user-profile-summary-reputation-item [loading]="true">
            </app-user-profile-summary-reputation-item>
          </fl-col>
        </fl-grid>
      </fl-bit>
    </ng-template>
  `,
  styleUrls: ['./user-profile-summary-reputation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileSummaryReputationComponent {
  FontColor = FontColor;
  FontType = FontType;
  TextSize = TextSize;
  Margin = Margin;
  RoleApi = RoleApi;

  @Input() isDeloitteUserProfile$: Rx.Observable<boolean>;
  @Input() userReputation$: Rx.Observable<UserReputation>;
}
